import { useMutation } from "@apollo/client";
import compact from "lodash/compact";
import uniqBy from "lodash/uniqBy";
import { FC, Ref, useState } from "react";
import {
  TopicDataFragmentFragment,
  TopicNodeNewPageFragmentFragment,
  TopicSidebarTopicNodeFragment,
} from "types/graphql-schema";

import { errorNotificationVar } from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import UserCombobox from "@components/user-combobox/user-combobox";
import {
  UserComboboxOption,
  UserComboboxOptionType,
  UserComboboxUserOption,
} from "@components/user-combobox/user-combobox-list";
import { graphqlNone } from "@helpers/constants";

import updateTopicAssigneeMutation from "./graphql/update-topic-assignee-mutation";

const TopicAssignee = ({
  topic,
  assignableUsers = [],
  size = ButtonSize.large,
}: {
  topic:
    | TopicNodeNewPageFragmentFragment
    | TopicSidebarTopicNodeFragment
    | TopicDataFragmentFragment;
  children?: FC<{
    disabled?: boolean;
    setReferenceElement?: Ref<HTMLButtonElement>;
  }>;
  assignableUsers?: any[];
  size?: ButtonSize;
}) => {
  const [query, setQuery] = useState("");
  const [saveTopic] = useMutation(updateTopicAssigneeMutation, {
    update(cache, { data: { createOrUpdateTopic } }) {
      const topicId = cache.identify(topic);
      if (topicId) {
        cache.modify({
          id: topicId,
          fields: {
            assignee() {
              return createOrUpdateTopic.topic.assignee;
            },
          },
        });
      }
    },
    onError() {
      errorNotificationVar({
        description: "The topic assignee could not be saved.",
      });
    },
  });

  const handleChangeAssignee = (assignee: UserComboboxOption) => {
    saveTopic({
      variables: { topicId: topic.id, assignee: assignee.id },
      optimisticResponse: {
        createOrUpdateTopic: {
          topic: {
            ...topic,
            assignee: assignee.id === graphqlNone ? null : assignee,
          },
          __typename: "CreateOrUpdateTopicMutation",
        },
      },
    });
  };

  const emptyOption: UserComboboxUserOption = {
    id: graphqlNone,
    email: "Unassigned",
    name: "Unassigned",
    avatar: "",
    type: UserComboboxOptionType.USER,
  };
  const allAssignableUsers = compact([
    emptyOption,
    topic.assignee,
    ...assignableUsers,
  ]);
  const options: UserComboboxUserOption[] = uniqBy(
    allAssignableUsers,
    ({ id }) => id,
  ).map((user) => ({ type: UserComboboxOptionType.USER, ...user }));
  const selectedOption: UserComboboxUserOption = topic.assignee
    ? { type: UserComboboxOptionType.USER, ...topic.assignee }
    : emptyOption;

  return (
    <UserCombobox
      aria-label="Action item people dropdown"
      query={query}
      value={selectedOption}
      options={options}
      disabled={!topic.canUpdate.permission}
      onChangeValue={handleChangeAssignee}
      onChangeQuery={setQuery}
      containerClassName="flex"
    >
      <UserCombobox.Button theme={buttonTheme.iconGray} square size={size}>
        <Avatar user={topic.assignee} size="4" />
      </UserCombobox.Button>
    </UserCombobox>
  );
};

export default TopicAssignee;
